/**
 * Client breadcrumb trail — the recent nav / click / console history attached to a client error report
 * (see lib/log-error), so a crash carries the steps that led up to it. A tiny in-memory ring buffer,
 * dependency-free, never throws. Ported from medicos, extended to also capture clicks + console.error/warn.
 *
 * NEVER captures keystrokes or input values — only route hrefs, the accessible label of a clicked
 * control, and console message text. Best-effort: a lost breadcrumb is just a gap in the trail.
 */
import { registerClickSink } from './user-action-capture'

export interface Breadcrumb {
  t: number
  type: string
  message: string
}

const MAX_BREADCRUMBS = 30
const MAX_MESSAGE_LEN = 500

let ring: Breadcrumb[] = []

/** Record one breadcrumb. `type` is a coarse category ('nav'|'click'|'console'); `message` short detail. */
export function addBreadcrumb(type: string, message: string): void {
  ring.push({ t: Date.now(), type, message: message.slice(0, MAX_MESSAGE_LEN) })
  if (ring.length > MAX_BREADCRUMBS) ring = ring.slice(-MAX_BREADCRUMBS)
}

/** A copy of the current trail (oldest first), for attaching to an error report. */
export function getBreadcrumbs(): Breadcrumb[] {
  return ring.slice()
}

let installed = false

/**
 * Install passive click + console capture into the breadcrumb ring. Clicks come from the shared
 * capture pipeline (lib/user-action-capture); navigation breadcrumbs are added separately from the
 * router (see router.tsx). Call once on the client; no-op on the server / if repeated.
 */
export function installBreadcrumbCapture(): void {
  if (installed || typeof window === 'undefined') return
  installed = true

  registerClickSink((label) => addBreadcrumb('click', label))

  for (const level of ['error', 'warn'] as const) {
    const original = console[level].bind(console)
    console[level] = (...args: unknown[]): void => {
      original(...args)
      try {
        const message = args
          .map((a) => (a instanceof Error ? `${a.name}: ${a.message}` : typeof a === 'string' ? a : String(a)))
          .join(' ')
        addBreadcrumb(`console.${level}`, message)
      } catch {
        // swallow
      }
    }
  }
}
